import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auditAPI, userAPI } from '../services/api';

const AuditLogs = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    userId: '',
    action: '',
    entityType: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchUsers();
  }, [filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await auditAPI.getLogs(filters);
      setLogs(data.logs || []);
      setPagination(data.pagination || { total: 0, hasMore: false });
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await auditAPI.getStats(filters.startDate, filters.endDate);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userAPI.getUsers();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, offset: 0 });
  };

  const handleNextPage = () => {
    setFilters({ ...filters, offset: filters.offset + filters.limit });
  };

  const handlePrevPage = () => {
    setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) });
  };

  const resetFilters = () => {
    setFilters({
      limit: 50,
      offset: 0,
      userId: '',
      action: '',
      entityType: '',
      startDate: '',
      endDate: ''
    });
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create': return { bg: '#d1fae5', text: '#065f46', border: '#10b981' };
      case 'update': return { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' };
      case 'delete': return { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' };
      case 'login': return { bg: '#f3e8ff', text: '#6b21a8', border: '#a855f7' };
      case 'download_pdf': return { bg: '#fef3c7', text: '#92400e', border: '#fbbf24' };
      case 'send_email': return { bg: '#e0e7ff', text: '#3730a3', border: '#6366f1' };
      default: return { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
    }
  };

  const formatEntityType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';
  };

  const formatAction = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Dashboard</button>
              <button onClick={() => navigate('/pallets')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Pallet Sheets</button>
              <button onClick={() => navigate('/reports')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Reports</button>
              {user?.role === 'admin' && (
                <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#10b981', cursor: 'pointer', fontWeight: '600' }}>Admin</button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} style={{ padding: '8px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h2 style={{ marginBottom: '30px' }}>Audit Trail</h2>

        {/* Statistics Cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Recent Activity (24h)</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{stats.recentActivityCount}</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Total Logs</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#374151' }}>{pagination.total}</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Top Action</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#374151', textTransform: 'capitalize' }}>
                {stats.actionStats?.[0]?.action || 'N/A'} ({stats.actionStats?.[0]?.count || 0})
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Filters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>User</label>
              <select value={filters.userId} onChange={(e) => handleFilterChange('userId', e.target.value)} style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}>
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>Action</label>
              <select value={filters.action} onChange={(e) => handleFilterChange('action', e.target.value)} style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}>
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="download_pdf">Download PDF</option>
                <option value="send_email">Send Email</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>Entity Type</label>
              <select value={filters.entityType} onChange={(e) => handleFilterChange('entityType', e.target.value)} style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}>
                <option value="">All Types</option>
                <option value="pallet_sheet">Pallet Sheet</option>
                <option value="user">User</option>
                <option value="dropdown_list">Dropdown List</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>Start Date</label>
              <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>End Date</label>
              <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }} />
            </div>
          </div>
          <button onClick={resetFilters} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '2px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Reset Filters</button>
        </div>

        {/* Logs Table */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading audit logs...</div>
          ) : logs.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Timestamp</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>User</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Action</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Entity Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Entity ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const colors = getActionBadgeColor(log.action);
                      return (
                        <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', color: '#374151', whiteSpace: 'nowrap' }}>
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ fontWeight: '500', color: '#374151' }}>{log.user_name || 'System'}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{log.user_email}</div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: colors.bg,
                              color: colors.text,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              {formatAction(log.action)}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: '#374151' }}>{formatEntityType(log.entity_type)}</td>
                          <td style={{ padding: '12px', color: '#6b7280', fontFamily: 'monospace' }}>#{log.entity_id}</td>
                          <td style={{ padding: '12px', color: '#6b7280', fontSize: '12px' }}>{log.ip_address || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{ padding: '16px 20px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Showing {filters.offset + 1} - {filters.offset + logs.length} of {pagination.total}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handlePrevPage} disabled={filters.offset === 0} style={{ padding: '6px 12px', backgroundColor: filters.offset === 0 ? '#f3f4f6' : 'white', color: filters.offset === 0 ? '#9ca3af' : '#374151', border: '2px solid #e5e7eb', borderRadius: '6px', cursor: filters.offset === 0 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>Previous</button>
                  <button onClick={handleNextPage} disabled={!pagination.hasMore} style={{ padding: '6px 12px', backgroundColor: !pagination.hasMore ? '#f3f4f6' : 'white', color: !pagination.hasMore ? '#9ca3af' : '#374151', border: '2px solid #e5e7eb', borderRadius: '6px', cursor: !pagination.hasMore ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>Next</button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No audit logs found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
